import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { MeshEdit, MeshProposal } from './threeDApi';

// WHAT HAS HAPPENED TO THIS MESH, drawn like a log rather than stacked like a feed.
//
// THE TRUNK IS THE EDIT STACK — every change actually made, in the order it was made,
// whatever made it: a rescale, one of the two hand-written repairs off the menu, a saved
// library transformation, a written one. That is the mesh's history, and it is where an
// edit comes back off: pressing a dot and choosing Undo is the whole of undoing now. It was
// visible only as three words on a button below, which could reach the newest entry alone.
//
// It used to be the proposals instead, which meant a menu repair changed the geometry and
// left no mark, while a proposal nobody applied got a dot of its own. Two histories that
// only partly overlapped, and neither of them answered "what has been done to this".
//
// THE BRANCHES ARE THE PROPOSALS THAT WERE NOT APPLIED. They belong somewhere — each was a
// paid call and half a minute — but not on the trunk, because nothing in the geometry came
// of them. They hang off the point they were asked at: from the edit the mesh was on, or
// from the proposal they refine, which is what "build on this" means.
//
// Filled means it is IN the mesh. Every trunk dot is; a branch dot never is, by definition.
//
// PRESSING A TRUNK DOT SHOWS THE MESH AS IT STOOD THERE, and opens the proposal behind it
// if there was one. The two go together and most dots do both — a rescale has no proposal
// and still has a mesh; a branch has a proposal and no mesh, because nothing was applied
// and there is therefore nothing to go and look at.
//
// That the mesh is there at all costs nothing: every overwrite already copies aside what it
// replaced, which is what Undo moves back. Those copies were reachable only BY undoing, so
// "what did this look like before I shortened it" meant taking the change out and putting
// it back, with a repair in between that might be refused. Now it is a fetch.
//
// TWO RINGS, because looking and being are different things now.
//
// EMERALD IS THE MESH AS IT STANDS: the end of the trunk, or the origin if nothing has been
// done yet. Every Apply lands on it and nowhere else — a branch dot can be opened and
// applied FROM, but what comes out is the current mesh plus that code, not a return to the
// point the branch left.
//
// WHITE IS WHAT THE VIEWER IS SHOWING, and it only appears when that is not the live mesh.
// Looking three edits back does not move the mesh, so the emerald ring must not move with
// you — being back there with nothing saying so is how somebody presses Apply expecting it
// to land where they are looking.

// HOW FAR APART THE DOTS STAND — the widest they ever do, and the closest they are pushed.
//
// 112 is what a two-word name wants under it, and it was the only number: the rail was that
// wide per dot however many there were, so a history of nine ran to 1008px inside a modal
// with about 990 to give, and the ninth dot — the newest, the one somebody had just made —
// was the one off the right-hand edge. The box scrolls, so nothing was lost; it was simply
// not there until you thought to drag it.
//
// So the pitch is fitted to the room instead, down to 72. Below that the words under a dot
// wrap to more lines than the entry is worth, and the rail is better scrolling than
// unreadable — which is what the floor is: past it the box goes back to scrolling, as it
// always did, with a dozen entries rather than with five.
const COL = 112;
const MIN_COL = 72;
const LANE = 52; // px between branch lanes.
const DOT = 16;
// 2px, not a hairline. This is the spine of the thing — at 1px it read as a rule somebody
// had drawn under the dots rather than as what joins them.
const LINE = 2;
// ROOM UNDER THE LOWEST DOT FOR ITS CAPTION. The captions wrap rather than ending in an
// ellipsis, so this is the tallest one and not the only line of it: four lines of 11px at
// leading-tight, plus the gap between the dot and the first of them. It was 22 — one line —
// and the box has `overflow-x-auto`, which makes the OTHER axis auto too, so anything longer
// than that was not merely tight, it was scrolled out of sight.
//
// FOUR LINES IS THE MESH'S RAIL, where a dot can be called "bois d'hévéa massif, foncé ·
// grain kept". The retouch panel's names are two words and never wrap, and reserving four
// lines there was 40 pixels of empty blue under every dot in a panel that is fighting for
// the height. So it is a prop, and the caller that knows its labels are short says so.
const CAPTION = 62;
// WRAPPED, a row's pitch (see perRow), and the gap between two rows the trunk runs back through.
const ROW_COL = 96;
const ROW_GAP = 16;

// One dot: what it is, where it sits, and what opens when it is pressed.
type Node = {
  key: string;
  label: string;
  // THE COLOUR IT PAINTED, on the one kind of edit that has one. A colour op is named after
  // the material it applied — "bois d'hévéa massif, foncé" — which is the right name and is
  // indistinguishable from having named a material. The swatch is what says it was a colour.
  tint?: string;
  // The proposal behind it, when there is one — an applied one on the trunk, or an
  // unapplied one out on a branch.
  proposal?: MeshProposal;
  // Whether this is in the geometry as it stands. Every edit is; no branch is.
  inMesh: boolean;
  // Greyed: see `faded`.
  faded?: boolean;
  // Undone by hand, the k-th of `undone`.
  undoneAt?: number;
  // See `orderOf`.
  order?: string | null;
  // WHICH STATE OF THE MESH THIS DOT IS, as a count of edits applied: 0 for the origin,
  // i+1 for the i-th edit. Undefined on a branch, and that is the whole rule — a proposal
  // nobody applied produced no geometry, so there is no mesh to go and look at.
  meshAt?: number;
  when: number;
  parent?: string;
  col: number;
  lane: number;
};

const at = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0);

// THE CAPITAL IS PUT ON HERE AND NOT IN THE WORDS. Every label arrives lower-cased, because
// the same string is read out mid-sentence by an Undo button — "Undo felt, grey" in the
// retouch panel. Standing on its own under a dot it is a caption, and a caption
// starts with a capital, so the one place that difference is true is the one that draws it.
//
// IN JS AND NOT IN CSS. `first-letter:uppercase` is the obvious spelling and it does nothing
// here: ::first-letter applies to block boxes, and this is an inline run that a colour swatch
// sometimes opens.
const capitalised = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export function MeshProposalRail({
  edits, proposals, labelOf, tintOf, origin, openId, onOpen, viewingAt = null, onView,
  onDelete, onDeleteMany, caption = CAPTION, shown, faded, undone = [], onUndone, viewingUndone = null, orderOf, onHide,
}: {
  // The mesh's own history, oldest first — params.edits, exactly as the server keeps it.
  edits: MeshEdit[];
  // Everything ever proposed for it, newest first, applied or not.
  proposals: MeshProposal[];
  // What an edit is called. The same words the retouch panel's Undo button uses, and the
  // same function names both — see editName.
  labelOf: (edit: MeshEdit) => string;
  // The colour an edit painted, where it painted one. See Node.tint.
  tintOf?: (edit: MeshEdit) => string | undefined;
  // What the mesh WAS before anything was done to it — the reconstruction or the converted
  // file. Drawn as the first dot, because a history with no beginning starts mid-sentence.
  origin: { label: string; when: string | null };
  openId: number | null;
  onOpen: (id: number) => void;
  // Which state the VIEWER is showing, as an edit count, or null for the live mesh. A
  // different fact from where the mesh is — that is always the end of the trunk — and the
  // reason the two get their own rings.
  viewingAt?: number | null;
  // Go and look at a state of the mesh. Called with the dot's own edit count; a caller
  // that gets the live one back is being told to go back to the live mesh.
  onView?: (at: number) => void;
  // TAKE THIS ENTRY OFF THE RUN — the other half of what a dot's menu offers. One of the
  // two is always null: a trunk dot is a state of the mesh and hands its edit count, a
  // branch dot is a proposal nobody applied and hands its id. What each means is the
  // caller's business, which is where the routes are.
  onDelete?: (what: { meshAt: number | null; proposalId: number | null }) => void;
  // TAKE SEVERAL CHANGES OFF AT ONCE — the ones a box dragged over the trunk caught, as edit
  // indices (a dot's edit count less one), oldest first. Absent, the rail offers no box.
  onDeleteMany?: (indices: number[]) => void;
  // WHICH CHANGES ARE DRAWN, where a caller leaves some out (a joint's, its parts' paint): those
  // keep their place in the history, and every dot its own count of edits.
  shown?: (edit: MeshEdit) => boolean;
  // WHICH ARE GREYED, drawn and pressable still: a joint's, the changes about its other joints.
  faded?: (edit: MeshEdit) => boolean;
  // THE CHANGES UNDONE BY HAND, kept on the trunk where they were made, crossed in red (Louis
  // 2026-10-29); their menu is the caller's — see onUndone. And which of them the viewer is showing.
  undone?: MeshEdit[];
  onUndone?: (k: number, what: 'see' | 'redo') => void;
  viewingUndone?: number | null;
  // A CHANGE'S PLACE AMONG ITS NEIGHBOURS, where the caller orders them otherwise than by time (a
  // joint's drillings, by their holes' ids): a run of changes that all have one is drawn in that order.
  orderOf?: (edit: MeshEdit) => string | null;
  // "Hide" on the dot being shown, where showing the live mesh is itself something to see (a joint's
  // zone marks): without it, Hide is "show the mesh as it stands".
  onHide?: () => void;
  // How much room to leave under the lowest dot for its name — see CAPTION, which is this
  // by default. Given smaller by a caller whose names are short.
  caption?: number;
}) {
  const { t } = useLanguage();
  // WHICH DOT HAS ITS MENU OPEN, by key, or none. Local and unremembered: it is a press in
  // progress, not a fact about the mesh, and the next thing anybody does ends it.
  const [menuAt, setMenuAt] = useState<string | null>(null);
  // THE CHANGES A DRAGGED BOX HAS CAUGHT, by dot key, and the box while it is being drawn — in
  // the rail's own pixels. Local like the menu: a selection in progress, not a fact about the
  // mesh. See the pointer handlers below.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [band, setBand] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const plane = useRef<HTMLDivElement>(null);
  // A dot's key is its position in the history, so a history that grew or shrank has moved
  // what every caught key names: the selection is put down rather than left pointing elsewhere.
  useEffect(() => { setPicked(new Set()); }, [edits.length]);
  // HOW MUCH ROOM THERE IS, measured rather than assumed. The rail is drawn inside a modal
  // whose width is the window's, so there is no constant to compute the pitch from — and
  // watched rather than read once, because the modal is resized by resizing the browser and a
  // pitch fitted to the width it had at mount is a pitch fitted to nothing.
  const host = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState(0);
  useEffect(() => {
    const el = host.current;
    if (!el) return undefined;
    const measure = () => setRoom(el.clientWidth);
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, []);
  const nodes = useMemo<Node[]>(() => {
    // Which proposal, if any, each edit came from: by the id recorded on it, and failing
    // that by the exact code it ran — the link the older edits do not carry.
    const byId = new Map(proposals.map((p) => [p.id, p]));
    const byCode = new Map(proposals.filter((p) => p.code).map((p) => [p.code, p]));
    const behind = (e: MeshEdit) =>
      (e.proposalId != null ? byId.get(e.proposalId) : undefined)
      ?? (e.code ? byCode.get(e.code) : undefined);

    // THE STARTING POINT. Every dot after this is something done TO it, and without it the
    // first edit looks like the mesh's own origin — and, worse, a proposal made before any
    // edit had nothing to hang off, so it drew no line at all.
    const root: Node = {
      key: 'root',
      label: origin.label,
      inMesh: true,
      // Nothing has been done to it yet, which is what zero edits means.
      meshAt: 0,
      // Before everything by construction, not by its timestamp: a run's createdAt is the
      // moment the file landed, and an edit stack restored from an older job can carry
      // times that predate nothing in particular.
      when: -Infinity,
      col: 0,
      lane: 0,
    };

    const trunk: Node[] = edits.flatMap((e, i) => (shown && !shown(e) ? [] : [{
      key: `e${i}`,
      label: labelOf(e),
      faded: faded?.(e),
      order: orderOf?.(e),
      tint: tintOf?.(e),
      proposal: behind(e),
      inMesh: true,
      // AFTER this edit, not before it: the dot is the change, and what you want to see by
      // pressing it is what the change produced.
      meshAt: i + 1,
      when: at(e.at) || i,
      col: 0,
      lane: 0,
    }]));
    const crossed: Node[] = undone.flatMap((e, k) => (shown && !shown(e) ? [] : [{
      key: `u${k}`,
      label: labelOf(e),
      faded: faded?.(e),
      order: orderOf?.(e),
      inMesh: true,
      undoneAt: k,
      when: at(e.at) || k,
      col: 0,
      lane: 0,
    }]));
    const applied = new Set(trunk.map((n) => n.proposal?.id).filter(Boolean));

    // The ones that never touched the mesh. Anchored to what they were asked ABOUT: the
    // proposal they refine, or the last edit that had happened when they were made.
    const branches: Node[] = proposals
      .filter((p) => !applied.has(p.id))
      .sort((a, b) => at(a.createdAt) - at(b.createdAt))
      .map((p) => ({
        key: `p${p.id}`,
        label: p.name || '—',
        proposal: p,
        inMesh: false,
        when: at(p.createdAt),
        col: 0,
        lane: 0,
      }));

    const all = [root, ...trunk, ...crossed, ...branches].sort((a, b) => a.when - b.when);
    // EACH RUN OF ORDERED CHANGES, re-sorted in place by its order: one drilling then another, read
    // by their holes, whenever each was made.
    for (let i = 0; i < all.length;) {
      let j = i;
      while (j < all.length && all[j].order != null) j += 1;
      if (j - i > 1) {
        const run = all.slice(i, j).sort((a, b) => a.order!.localeCompare(b.order!, undefined, { numeric: true }));
        all.splice(i, j - i, ...run);
      }
      i = Math.max(j, i + 1);
    }
    const byKey = new Map(all.map((n) => [n.key, n]));
    const fromProposal = new Map(all.map((n) => [n.proposal?.id ?? -1, n]));

    all.forEach((n, i) => {
      n.col = i;
      if (n.inMesh) return;
      const p = n.proposal;
      const parent = (p?.basedOnId != null ? fromProposal.get(p.basedOnId) : undefined)
        // Failing that, the last thing that had been done to the mesh when it was asked —
        // and failing THAT, the mesh itself, which is what a proposal made before any edit
        // was asked about. It used to find nothing there and draw a dot joined to nothing.
        ?? [...all].filter((x) => x.inMesh && x.when <= n.when).pop()
        ?? root;
      if (parent && parent.key !== n.key) {
        n.parent = parent.key;
        n.lane = parent.lane + 1;
      } else {
        n.lane = 1;
      }
    });
    // Second pass for a branch off a branch: its parent's lane is only final once every
    // node has been placed, and a refinement of a refinement is two lanes down.
    all.forEach((n) => {
      const parent = n.parent ? byKey.get(n.parent) : undefined;
      if (parent) n.lane = parent.lane + 1;
    });
    return all;
  }, [edits, proposals, labelOf, tintOf, origin.label, origin.when, shown, faded, undone, orderOf]);

  if (!nodes.length) return null;

  const lanes = Math.max(...nodes.map((n) => n.lane), 0) + 1;
  // THE PITCH THIS RAIL IS ACTUALLY DRAWN AT: as wide as it likes while the dots fit, and
  // squeezed towards MIN_COL as they accumulate. Full width until the box has been measured,
  // so the first paint is the old one rather than everything stacked at the floor.
  //
  // AND PAST THE FLOOR, ROWS (Louis 2026-10-29), never a scroll: as many dots a row as fit at
  // ROW_COL. TO AND FRO (Louis 2026-11-02): every other row read right to left, so the trunk turns
  // down at the end of one, round, into the start of the next — dots on the way back too, and no
  // bare line running back under a row.
  const fits = room <= 0 || nodes.length * MIN_COL <= room;
  const perRow = fits ? nodes.length : Math.max(1, Math.floor(room / ROW_COL));
  const col = !fits ? Math.floor(room / perRow) : room > 0
    ? Math.max(MIN_COL, Math.min(COL, Math.floor(room / nodes.length)))
    : COL;
  // AND THE ROOM UNDER THE LOWEST DOT, which has to grow as the columns narrow: the captions
  // wrap, so the same words in a column a third narrower take about a third more lines. It is
  // a proportion and not a measurement — measuring six wrapped captions to lay out the box
  // that contains them is a second pass for a number the floor above already bounds, and the
  // most this can ask for is 112/72 of what a caller said it needed.
  const capRoom = Math.round(caption * (COL / col));
  const rowH = lanes * LANE + capRoom + ROW_GAP;
  const rowOf = (c: number) => Math.floor(c / perRow);
  const x = (c: number) => {
    const at = c % perRow;
    return (rowOf(c) % 2 ? perRow - 1 - at : at) * col + col / 2;
  };
  const y = (c: number, lane: number) => rowOf(c) * rowH + lane * LANE + LANE / 2;
  const rows = rowOf(Math.max(...nodes.map((n) => n.col))) + 1;
  // A LINE, across or down, from two points on it.
  const seg = (k: string, x0: number, y0: number, x1: number, y1: number, tone: string) => (
    <div
      key={k}
      className={`absolute rounded ${tone}`}
      style={{
        left: Math.min(x0, x1) - (x0 === x1 ? LINE / 2 : 0), top: Math.min(y0, y1) - (y0 === y1 ? LINE / 2 : 0),
        width: x0 === x1 ? LINE : Math.abs(x1 - x0), height: y0 === y1 ? LINE : Math.abs(y1 - y0),
      }}
    />
  );
  const byKey = new Map(nodes.map((n) => [n.key, n]));
  const menu = menuAt ? byKey.get(menuAt) ?? null : null;
  // Whether the dot whose menu is open is the state the viewer is showing.
  const seen = !!menu && menu.meshAt != null && viewingAt != null && menu.meshAt === viewingAt;
  // WHETHER THERE IS ANYTHING TO TAKE. The origin is the mesh as it was made — undoing
  // it would mean unmaking the run, which is what deleting the run is for and is a button
  // elsewhere. Everything else is either an edit or a proposal.
  const removable = (n: Node) =>
    !!onDelete && ((n.meshAt != null && n.meshAt > 0) || !!n.proposal);
  const trunk = nodes.filter((n) => n.inMesh);
  const trunkCols = trunk.map((n) => n.col);

  // A BOX DRAGGED OVER THE TRUNK SELECTS WHAT IT COVERS — changes only: the origin is the mesh
  // as made, and a proposal nobody applied is not a change to undo. Started on empty rail and
  // never on a dot, so a press on a dot still opens its menu. A press that barely moved clears
  // the selection, which is how it is put down.
  const selectable = (n: Node) => n.inMesh && n.meshAt != null && n.meshAt > 0;
  const local = (e: React.PointerEvent) => {
    const r = plane.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!onDeleteMany || e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    const p = local(e);
    e.currentTarget.setPointerCapture(e.pointerId);
    setBand({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!band) return;
    const p = local(e);
    setBand({ ...band, x1: p.x, y1: p.y });
  };
  const onPointerUp = () => {
    if (!band) return;
    const [l, r] = [Math.min(band.x0, band.x1), Math.max(band.x0, band.x1)];
    const [tp, bt] = [Math.min(band.y0, band.y1), Math.max(band.y0, band.y1)];
    setBand(null);
    if (r - l < 4 && bt - tp < 4) {
      setPicked(new Set());
      return;
    }
    // A dot is caught when the box reaches any of it, not only its centre.
    const half = DOT / 2;
    setPicked(new Set(nodes.filter((n) => selectable(n)
      && x(n.col) + half >= l && x(n.col) - half <= r
      && y(n.col, n.lane) + half >= tp && y(n.col, n.lane) - half <= bt).map((n) => n.key)));
  };
  const pickedIndices = nodes
    .filter((n) => picked.has(n.key) && n.meshAt != null)
    .map((n) => n.meshAt! - 1)
    .sort((a, b) => a - b);
  // Where the mesh is: the last thing done to it, or the origin if nothing has been.
  const current = [...trunk].reverse().find((n) => n.undoneAt == null);

  return (
    <div ref={host} className="w-full">
      <div
        ref={plane}
        className={`relative mx-auto select-none ${onDeleteMany ? 'cursor-crosshair' : ''}`}
        style={{ width: Math.min(nodes.length, perRow) * col, height: rows * rowH - ROW_GAP }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setBand(null)}
      >
        {band && (
          <div
            className="pointer-events-none absolute z-10 rounded border border-rose-400 bg-rose-400/10"
            style={{
              left: Math.min(band.x0, band.x1), top: Math.min(band.y0, band.y1),
              width: Math.abs(band.x1 - band.x0), height: Math.abs(band.y1 - band.y0),
            }}
          />
        )}
        {/* The trunk: from the first edit to the last, through the dots and not under
            them. One line a row rather than a segment per gap, because what it means is "this
            is the mesh's history" and not "these two are adjacent" — and from the end of a row,
            out past its last caption and ROUND (Louis 2026-11-02) into the start of the next, which
            sits under it: the rows run to and fro (see x). */}
        {Array.from({ length: rows }, (_, r) => {
          const mine = trunkCols.filter((c) => rowOf(c) === r);
          if (!mine.length) return null;
          const first = mine[0];
          const last = mine[mine.length - 1];
          const next = trunkCols.find((c) => rowOf(c) > r);
          const tone = 'bg-slate-300';
          // THE TURN, on the side the row ends: a box open towards the dots, its outer corners round.
          const right = r % 2 === 0;
          const reach = col / 2 - 4;
          const top = y(last, 0) - LINE / 2;
          const height = next != null ? y(next, 0) - y(last, 0) + LINE : 0;
          const radius = Math.min(reach, height / 2);
          return [
            mine.length > 1 && seg(`t${r}`, x(first), y(first, 0), x(last), y(last, 0), tone),
            ...(next != null ? [
              <div
                key={`turn${r}`}
                className="absolute border-slate-300"
                style={{
                  left: right ? x(last) : x(last) - reach, top, width: reach, height,
                  borderStyle: 'solid', borderWidth: right ? `${LINE}px ${LINE}px ${LINE}px 0` : `${LINE}px 0 ${LINE}px ${LINE}px`,
                  borderRadius: right ? `0 ${radius}px ${radius}px 0` : `${radius}px 0 0 ${radius}px`,
                }}
              />,
              // AND ON TO IT, where the next row's first dot is not straight under this one's last.
              x(next) !== x(last) && seg(`i${r}`, x(last), y(next, 0), x(next), y(next, 0), tone),
            ] : []),
          ];
        })}

        {nodes.map((n) => {
          const parent = n.parent ? byKey.get(n.parent) : undefined;
          if (!parent) return null;
          // The elbow: down the parent's column, then across to this one.
          return (
            <div key={`edge-${n.key}`}>
              <div
                className="absolute rounded bg-slate-200"
                style={{
                  left: x(parent.col) - LINE / 2, top: Math.min(y(parent.col, parent.lane), y(n.col, n.lane)),
                  width: LINE, height: Math.abs(y(n.col, n.lane) - y(parent.col, parent.lane)),
                }}
              />
              <div
                className="absolute rounded bg-slate-200"
                style={{
                  left: Math.min(x(parent.col), x(n.col)), top: y(n.col, n.lane) - LINE / 2,
                  width: Math.abs(x(n.col) - x(parent.col)), height: LINE,
                }}
              />
            </div>
          );
        })}

        {nodes.map((n) => {
          const open = !!n.proposal && n.proposal.id === openId;
          // A dot does one of two things, and most trunk dots do both: open the proposal
          // behind it, and show the mesh as it stood there. Either is enough to be worth
          // pressing — a rescale has no proposal and still has a mesh, and a branch has a
          // proposal and no mesh.
          const canView = n.meshAt != null && !!onView;
          // AND A CHANGE THAT CAN BE TAKEN OFF, where nothing shows the mesh (a joint's part histories):
          // its menu is then the one thing it offers.
          const clickable = !!n.proposal || canView || removable(n) || (n.undoneAt != null && !!onUndone);
          const here = n.key === current?.key;
          // What the viewer is showing. Only ever true of a trunk dot, and never of the
          // live mesh — viewingAt is null there, which is what "you are looking at the
          // thing itself" means.
          const watching = n.undoneAt != null ? viewingUndone === n.undoneAt
            : n.meshAt != null && viewingAt != null && n.meshAt === viewingAt;
          return (
            <button
              key={n.key}
              type="button"
              disabled={!clickable}
              // A DOT ASKS BEFORE IT ACTS. It used to do its thing on the press, which is
              // right for looking and wrong for the other thing a dot can now do: pressing
              // one on a four-entry history and having four entries disappear is not a
              // gesture anybody should be able to make by aiming badly. So the press opens
              // the two words instead, and the destructive one has to be chosen.
              onClick={() => setMenuAt((was) => (was === n.key ? null : n.key))}
              title={[
                n.proposal?.instruction || n.label,
                here ? t('notice.3d.currentNode') : null,
              ].filter(Boolean).join(' — ')}
              // THE BUTTON IS THE DOT, and the caption hangs out of it: a column holding
              // both would centre on the middle of "dot plus caption", which is half a
              // caption below the dot the line has to pass through.
              className={`absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 transition-colors ${
                // GREYED IN SOLID COLOURS, not by transparency: a see-through dot shows the trunk through it.
                // UNDONE: white, its cross in red, a step back from the ones still in the mesh.
                n.undoneAt != null ? `bg-white ${n.faded ? 'border-red-100' : 'border-red-300'}`
                  : n.inMesh && n.faded ? 'border-blue-100 bg-blue-200 text-white'
                  : n.inMesh ? 'border-blue-400 bg-blue-500 text-white'
                  : 'border-slate-300 bg-white text-transparent'
              } ${clickable ? '' : 'cursor-default'} ${
                // TWO RINGS, TWO FACTS, and they are only ever on one dot at a time each.
                //
                // EMERALD IS WHERE THE MESH IS: the end of the trunk, the thing every Apply
                // lands on. It used to mark the open card, which the caption already says.
                //
                // WHITE IS WHAT YOU ARE LOOKING AT, and it exists because those came apart
                // the day a dot could show the mesh as it WAS. Looking at an earlier state
                // does not move the mesh, so the emerald ring must not move either — and
                // being three edits back with nothing saying so is how somebody presses
                // Apply expecting it to land where they are looking.
                // AND ROSE IS CAUGHT BY THE BOX, which outranks both: it is what the button under
                // the rail is about to take off.
                picked.has(n.key) ? 'ring-2 ring-rose-500 ring-offset-2 ring-offset-white'
                  : here ? 'ring-2 ring-blue-400/70 ring-offset-2 ring-offset-white'
                  // IN RED (Louis 2026-10-29), as the zone it is showing is drawn on the part.
                  : watching ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white' : ''
              }`}
              style={{ left: x(n.col), top: y(n.col, n.lane), width: DOT, height: DOT }}
            >
              {n.undoneAt != null ? <X className="h-2.5 w-2.5 text-red-600" strokeWidth={4} />
                : n.inMesh && <Check className="h-2.5 w-2.5" strokeWidth={4} />}
              <span
                // The open card's dot is named in emerald and in medium — the whole of how
                // "this is the one showing below" is said, now the ring means something else.
                // WRAPPED, NOT CUT OFF. "bois d'hévéa m…" names nothing: the material is the
                // whole of what a colour entry says it did, and the tail this was dropping is
                // the half that tells one wood from another. So the caption takes its whole
                // column and as many lines as the words do — the rail is taller by a line and
                // every entry can be read, which is what it is for.
                //
                // ITS WIDTH FOLLOWS THE COLUMN, which is now a number rather than a constant:
                // a caption left at 104px in a column squeezed to 72 would overlap its
                // neighbours on both sides, and the words that overlap are the ones a squeezed
                // rail makes hardest to read.
                className={`absolute left-1/2 top-[calc(100%+5px)] -translate-x-1/2 whitespace-pre-line text-center text-[11px] leading-tight break-words hyphens-auto ${
                  open ? 'font-medium text-blue-600'
                    : n.faded ? 'text-slate-300'
                    : n.inMesh ? 'text-slate-600'
                      : 'text-slate-400'
                }`}
                style={{ width: col - 8 }}
              >
                {/* THE COLOUR IT PAINTED, before its name. A colour op is named after the
                    material it applied, which is the right name and reads exactly like having
                    named a material — this is the half of the entry that says a colour
                    happened, and it says which one. */}
                {n.tint && (
                  <span
                    className="mr-1 inline-block h-2 w-2 rounded-[2px] border border-slate-300 align-middle"
                    style={{ backgroundColor: n.tint }}
                  />
                )}
                {capitalised(n.label)}
              </span>
            </button>
          );
        })}

        {/* ANYWHERE ELSE CLOSES IT. Under the menu and over everything else, so the next
            press either chooses something or puts the menu away — and never both, which is
            what a menu that stayed open while the dot beneath it was pressed again would
            allow.

            THE WHOLE WINDOW, not the rail. It covered `inset-0` of the box the dots are
            drawn in, which is a strip about ninety pixels tall in the middle of a modal:
            every click that was actually OUTSIDE the menu — the buttons above, the viewer,
            the page behind — missed the one thing listening for it, and the menu simply
            stayed open. Fixed rather than absolute because the box it lives in is the
            wrong size for the job by definition; it is the viewport that "elsewhere" means.

            It swallows that press, and on purpose: the click that dismisses a menu should
            dismiss the menu and do nothing else. */}
        {menu && (
          <button
            type="button"
            aria-label={t('notice.3d.dotClose')}
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setMenuAt(null)}
          />
        )}

        {/* WHAT PRESSING A DOT OFFERS: look at that state of the mesh, or take it off the
            run. Two words and no third — every other thing a dot can say about itself is
            already written under it. */}
        {menu && menu.undoneAt != null && (
          <div
            className="absolute z-20 flex -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white text-[11px] shadow-lg"
            style={{ left: x(menu.col), top: y(menu.col, menu.lane) + DOT }}
          >
            <button
              type="button"
              onClick={() => { onUndone?.(menu.undoneAt!, 'see'); setMenuAt(null); }}
              className="px-3 py-1.5 text-center text-slate-600 transition-colors hover:bg-slate-100"
            >
              {t(viewingUndone === menu.undoneAt ? 'notice.3d.dotHide' : 'notice.3d.dotSee')}
            </button>
            <button
              type="button"
              onClick={() => { onUndone?.(menu.undoneAt!, 'redo'); setMenuAt(null); }}
              className="border-t border-slate-200 px-3 py-1.5 text-center text-blue-600 transition-colors hover:bg-blue-50"
            >
              {t('notice.3d.dotRedo')}
            </button>
          </div>
        )}
        {menu && menu.undoneAt == null && (
          <div
            className="absolute z-20 flex -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white text-[11px] shadow-lg"
            style={{ left: x(menu.col), top: y(menu.col, menu.lane) + DOT }}
          >
            <button
              type="button"
              disabled={!(menu.meshAt != null && onView) && !menu.proposal}
              onClick={() => {
                // ALREADY SHOWN: pressed again, back to the mesh as it stands (Louis 2026-10-29).
                if (seen) (onHide ?? (() => onView?.(edits.length)))();
                else if (menu.meshAt != null) onView?.(menu.meshAt);
                if (menu.proposal && !seen) onOpen(menu.proposal.id);
                setMenuAt(null);
              }}
              className="px-3 py-1.5 text-center text-slate-600 transition-colors hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-transparent"
            >
              {t(seen ? 'notice.3d.dotHide' : 'notice.3d.dotSee')}
            </button>
            {removable(menu) && (
              <button
                type="button"
                // WHAT TAKING IT OFF IS CALLED, and it is two different things. A trunk dot
                // is IN the mesh, so removing it is an undo — the same word the panel used
                // to spell on a button of its own below, which this replaced: pressing the
                // newest dot and choosing this is exactly what that button did, and two
                // controls for one action is one too many. A branch dot was never applied,
                // so there is nothing to undo and what goes is the record of the proposal.
                title={menu.meshAt != null ? t('notice.3d.revertHint') : undefined}
                onClick={() => {
                  // A branch has no mesh to go back to, and `undefined` is not that answer —
                  // the caller reads null as "this dot is not a state of the mesh".
                  onDelete?.({
                    meshAt: menu.meshAt ?? null,
                    proposalId: menu.proposal?.id ?? null,
                  });
                  setMenuAt(null);
                }}
                className="border-t border-slate-200 px-3 py-1.5 text-center text-rose-600 transition-colors hover:bg-rose-50"
              >
                {menu.meshAt != null ? t('notice.3d.revert') : t('notice.3d.dotDelete')}
              </button>
            )}
          </div>
        )}
      </div>
      {/* WHAT THE BOX CAUGHT, AND THE ONE PRESS THAT UNDOES IT ALL. Said as a count, next to
          the rail it counts on; putting it down is a click on empty rail, or this. */}
      {pickedIndices.length > 0 && onDeleteMany && (
        <div className="mt-1 flex items-center justify-center gap-2 text-[11px]">
          <button
            type="button"
            onClick={() => {
              onDeleteMany(pickedIndices);
              setPicked(new Set());
            }}
            className="rounded-lg border border-rose-300 px-2.5 py-1 font-medium text-rose-600 transition-colors hover:bg-rose-50"
          >
            {t('notice.3d.revertMany').replace('{n}', String(pickedIndices.length))}
          </button>
          <button
            type="button"
            onClick={() => setPicked(new Set())}
            className="rounded-lg border border-slate-300 px-2.5 py-1 text-slate-600 transition-colors hover:bg-slate-100"
          >
            {t('notice.3d.pickClear')}
          </button>
        </div>
      )}
    </div>
  );
}
